// main imports
import React, { Component } from "react";
import type { Row } from "react-table";
import { History, Location } from "history";
import { FormattedMessage } from "react-intl";

// models
import type { IUpdateStockLocationReq, IGetStockLocationsRes } from "@/lib/backend/manufacturing2.generated.types";
import { STOCK_LOCATION_TAGS, STOCK_LOCATION_TAGS_ACCESS } from "@/lib/ManufacturingTags.generated";
import { STOCK_LOCATION_TAGS as STOCK_LOCATION_TAGS2 } from "@/lib/ManufacturingTagsEditor.generated";
import * as BusinessLogic from "@/lib/BusinessLogic";
import type { ITags } from "@/lib/Models";

// subcomponents
import ErrorComponent from "@/components/ErrorComponent";
import Toggle from "react-toggle";
import { translate } from "@/components/IntlProviderWrapper";

// backend
import { getBackend } from "@/lib/backend/Backend2";
import TagsTables from "@/components/react/TagsTables";
import {
    mapDataToCategories,
    mapDataToCustomTags,
    Columns,
    updateTagsConfig,
    onChangeUpdateTags,
    type TagData,
    type TagsData,
    type TagsConfig,
    type TagUpdateValue,
} from "@/components/react/ReactTableTags";

import UserDocumentationLink from "@/components/UserDocumentationLink";

import img_banner from "@/img/banner.png";
import { match } from "react-router-dom";

type ITagDisplay = {
    name: string;
    id: string;
};

type Props = {
    match: match<{ uuid?: string }>;
    history: History;
    location: Location;
};

export type State = {
    uuid: string;
    external_id: string;
    title: string;
    production_site: boolean;
    storage_site: boolean;
    tags: ITags;
    error: string | null;
    warning: string;
};

export default class StockLocationEdit extends Component<Props, State> {
    state: State = {
        uuid: "",
        external_id: "",
        title: "",
        production_site: false,
        storage_site: false,
        tags: {
            production_site: "false",
            storage_site: "false",
        },
        error: "",
        warning: "",
    };

    async componentDidMount() {
        const { uuid } = this.props.match.params;

        const { locations }: IGetStockLocationsRes = await getBackend().manufacturing.getStockLocations({});
        const stock_location = locations.filter(sl => sl.uuid === uuid);

        if (stock_location.length === 0) {
            this.setState({ error: `Invalid stock location ${uuid}` });
        } else {
            this.setState({
                uuid: uuid as string,
                external_id: stock_location[0].external_id,
                title: stock_location[0].title,
                production_site: stock_location[0].tags[STOCK_LOCATION_TAGS.production_site] === "true",
                storage_site: stock_location[0].tags[STOCK_LOCATION_TAGS.storage_site] === "true",
                tags: stock_location[0].tags,
            });
        }
    }

    validateShallow(): boolean {
        return this.state.title.length > 0 && this.state.external_id.length > 0;
    }

    handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        // read new values
        const target = event.currentTarget;
        let value: string | boolean = target.value;
        const name = target.name;

        // hack for toggle element
        if (Object.keys(STOCK_LOCATION_TAGS).includes(name)) {
            value = (target as HTMLInputElement).checked;
        }

        // update new datasource placeholder
        this.setState(prevState => ({
            ...prevState,
            [name]: value,
        }));

        // handle tags
        if (Object.keys(STOCK_LOCATION_TAGS).includes(name)) {
            let tags = this.state.tags || {};
            tags[name] = value.toString();
            this.setState({ tags: tags });
        }
    };

    /**
     * Handling submission of the form. Basic checks and saves with error handling.
     */
    handleSubmit = async () => {
        this.setState({ error: null });

        try {
            if (this.validateShallow()) {
                // update current object
                let stock_location: IUpdateStockLocationReq = {
                    uuid: this.state.uuid,
                    external_id: this.state.external_id,
                    title: this.state.title,
                    tags: this.state.tags,
                };
                await getBackend().manufacturing.updateStockLocation(stock_location);
                // force refresh cache to get new line group tags
                await BusinessLogic.forceManufacturingCacheRefresh();
                this.props.history.push("/digital-twin/resources/stock_locations/" + this.state.uuid);
            } else {
                this.setState({
                    warning: translate(
                        "Manufacturing.StockLocations.cannot_submit_missing_values",
                        "Cannot submit a form with missing values"
                    ),
                });
            }
        } catch (err: unknown) {
            this.setState({ error: (err as Error).message });
        }
    };

    renderTagField(tag: ITagDisplay) {
        return (
            <div className="form-group" key={tag.id}>
                <label>{tag.name}</label>
                <br />
                <Toggle name={tag.id} checked={this.state[tag.id]} onChange={this.handleInputChange} />
            </div>
        );
    }

    onChangeCell = (name: string) => (value: TagUpdateValue) => {
        this.setState({ tags: onChangeUpdateTags(this.state.tags, name, value) });
    };

    rowStyle = (row: Row<TagsData>): React.CSSProperties => {
        let style: React.CSSProperties = {};

        let mandatoryCell = row.cells.filter(cell => cell.column.id === "mandatory");

        if (mandatoryCell.length > 0) {
            mandatoryCell = mandatoryCell[0];

            if (mandatoryCell.column.id === "mandatory") {
                style.backgroundColor = "white";
            }

            if (
                mandatoryCell.value.value !== true &&
                mandatoryCell.row.values.tag &&
                mandatoryCell.row.values.tag.value === undefined
            ) {
                style.backgroundColor = "#eaeaea";
            }
        }

        return style;
    };

    getEditableTag = (element: TagData): boolean => {
        return (
            !Object.prototype.hasOwnProperty.call(element, "editable") ||
            (Object.prototype.hasOwnProperty.call(element, "editable") && element.editable !== false)
        );
    };

    getFilteredStockLocationTags = (): TagsConfig => {
        let STOCK_LOCATION_TAGS2_COPY = { ...STOCK_LOCATION_TAGS2 } as TagsConfig;
        const editable_values = STOCK_LOCATION_TAGS2_COPY["values"].filter(element => this.getEditableTag(element));
        STOCK_LOCATION_TAGS2_COPY["values"] = editable_values;
        return STOCK_LOCATION_TAGS2_COPY;
    };

    render() {
        return (
            <div key="ds_new">
                <section key="1">
                    <img src={img_banner} alt="banner" className="img-fluid-header banner_img" />
                </section>
                <section id="profile_edit" className="new_line" key="2">
                    <div className="container">
                        <div className="row">
                            <div className="col-md-12">
                                <div className="white_box">
                                    <h2 className="mb-4">
                                        <FormattedMessage
                                            id="Manufacturing.StockLocations.edit_stock_location"
                                            defaultMessage="Edit stock location"
                                        />
                                    </h2>
                                    <ErrorComponent msg={this.state.error} type="error" />
                                    <ErrorComponent msg={this.state.warning} type="warning" />

                                    <div className="tab-content">
                                        <div className="form-group">
                                            <label>
                                                <FormattedMessage id="common.title" defaultMessage="Title" />:
                                            </label>
                                            <input
                                                name="title"
                                                type="text"
                                                className="form-control"
                                                value={this.state.title}
                                                onChange={this.handleInputChange}
                                            />
                                        </div>

                                        <div className="form-group">
                                            <label>
                                                <FormattedMessage
                                                    id="common.external_id"
                                                    defaultMessage="External ID"
                                                />
                                                :
                                            </label>
                                            <input
                                                name="external_id"
                                                type="text"
                                                className="form-control"
                                                value={this.state.external_id}
                                                onChange={this.handleInputChange}
                                            />
                                        </div>
                                    </div>
                                    <div className="text-right mb-3">
                                        <UserDocumentationLink link_key={"stock_locations"} />
                                    </div>
                                    <TagsTables
                                        categories={mapDataToCategories(
                                            this.state.tags,
                                            updateTagsConfig(this.getFilteredStockLocationTags()),
                                            STOCK_LOCATION_TAGS_ACCESS
                                        )}
                                        columns={Columns}
                                        onChangeCell={this.onChangeCell}
                                        rowStyle={this.rowStyle}
                                        customTags={mapDataToCustomTags(
                                            this.state.tags,
                                            updateTagsConfig(this.getFilteredStockLocationTags())
                                        )}
                                    />
                                    <div className="button-list button-list-entity-edit order-last ml-auto">
                                        <button className="btn btn-primary" onClick={this.handleSubmit}>
                                            <FormattedMessage id="common.save" defaultMessage="Save" />
                                        </button>
                                        <button
                                            className="btn btn-outline-secondary"
                                            onClick={() => this.props.history.goBack()}
                                        >
                                            <FormattedMessage id="common.back" defaultMessage="Back" />
                                        </button>
                                    </div>
                                </div>
                                <div className="space30"></div>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        );
    }
}
